import React, { useState, useEffect } from "react";
import { Loader2, X, AlertTriangle } from "lucide-react";
import { fetchQuizById, updateQuiz } from "../../../api/quizeApi";

const UpdateQuiz = ({ isOpen, onClose, quizId, onQuizUpdated, setRefreshFlag }) => {
  const [formData, setFormData] = useState({
    quotaId: "",
    title: "",
    timeLimit: "",
    allowedAttempts: "",
    status: ""
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [initialData, setInitialData] = useState(null);

  // Fetch quiz details
  useEffect(() => {
    if (isOpen && quizId) {
      const getQuiz = async () => {
        setLoading(true);
        setSubmitError("");
        try {
          const data = await fetchQuizById(quizId);
          const formattedData = {
            quotaId: data.quota_id || "",
            title: data.title || "",
            timeLimit: data.time_limit || "",
            allowedAttempts: data.allowed_attempt_count || "",
            status: data.status?.toString() || "1"
          };
          setFormData(formattedData);
          setInitialData(formattedData);
        } catch (err) {
          setSubmitError("Failed to fetch quiz data. Please try again.");
          console.error("Failed to fetch quiz data", err);
        } finally {
          setLoading(false);
        }
      };

      getQuiz();
    }
  }, [isOpen, quizId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear error for this field when user edits
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }
    
    if (!formData.timeLimit) {
      newErrors.timeLimit = "Time limit is required";
    } else if (parseInt(formData.timeLimit) <= 0) {
      newErrors.timeLimit = "Time limit must be greater than 0";
    }
    
    if (!formData.allowedAttempts) {
      newErrors.allowedAttempts = "Allowed attempts is required";
    } else if (parseInt(formData.allowedAttempts) <= 0) {
      newErrors.allowedAttempts = "Allowed attempts must be greater than 0";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    setSubmitError("");
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      const result = await updateQuiz(quizId, {
        title: formData.title,
        timeLimit: parseInt(formData.timeLimit),
        allowedAttemps: parseInt(formData.allowedAttempts),
        status: parseInt(formData.status),
        quotaId: parseInt(formData.quotaId) || null
      });
      onQuizUpdated(result);
      setRefreshFlag((prev) => !prev);
      onClose();
    } catch (error) {
      setSubmitError("Failed to update quiz. Please check your inputs and try again.");
      console.error("Update failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = () => {
    if (!initialData) return false;
    return JSON.stringify(initialData) !== JSON.stringify(formData);
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="flex items-center text-white">
          <Loader2 className="w-8 h-8 mr-3 animate-spin" />
          Loading Quize details...
        </div>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md relative animate-fadeIn">
        <div className="flex justify-between items-center mb-6 border-b pb-3">
          <h2 className="text-xl font-semibold text-gray-800">Update Quiz</h2>
          <button 
            className="text-gray-500 hover:text-gray-700 transition-colors"
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {submitError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 text-red-700">
            <AlertTriangle size={16} />
            <p className="text-sm">{submitError}</p>
          </div>
        )}

        <div className="space-y-4">
          <div className="space-y-1">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
              Quiz Title *
            </label>
            <input
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter quiz title"
              className={`w-full border ${errors.title ? 'border-red-500' : 'border-gray-300'} px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            />
            {errors.title && <p className="text-red-600 text-xs mt-1">{errors.title}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label htmlFor="timeLimit" className="block text-sm font-medium text-gray-700">
                Time Limit (minutes) *
              </label>
              <input
                id="timeLimit"
                name="timeLimit"
                value={formData.timeLimit}
                onChange={handleChange}
                type="number"
                min="1"
                placeholder="e.g. 30"
                className={`w-full border ${errors.timeLimit ? 'border-red-500' : 'border-gray-300'} px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors.timeLimit && <p className="text-red-600 text-xs mt-1">{errors.timeLimit}</p>}
            </div>

            <div className="space-y-1">
              <label htmlFor="allowedAttempts" className="block text-sm font-medium text-gray-700">
                Allowed Attempts *
              </label>
              <input
                id="allowedAttempts"
                name="allowedAttempts"
                value={formData.allowedAttempts}
                onChange={handleChange}
                type="number"
                min="1"
                placeholder="e.g. 3"
                className={`w-full border ${errors.allowedAttempts ? 'border-red-500' : 'border-gray-300'} px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors.allowedAttempts && <p className="text-red-600 text-xs mt-1">{errors.allowedAttempts}</p>}
            </div>
          </div>

          <div className="space-y-1">
            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
              Status
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>

          <div className="space-y-1 hidden">
            <label htmlFor="quotaId" className="block text-sm font-medium text-gray-700">
              Quota ID
            </label>
            <input
              id="quotaId"
              name="quotaId"
              value={formData.quotaId}
              onChange={handleChange}
              type="number"
              placeholder="Enter quota ID (optional)"
              className="w-full border border-gray-300 px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 w-full"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className={`px-4 py-2 rounded-md text-white w-full flex justify-center items-center ${hasChanges() ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-400 cursor-not-allowed'}`}
            disabled={loading || !hasChanges()}
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin mr-2" />
                <span>Updating...</span>
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateQuiz;